import React, { useState } from "react";
import {
  Row,
  Col,
  Spin,
  Form,
  Card,
  InputNumber,
  Button,
} from "antd";
import { InfoCircleOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { connect } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import MainLayout from "../../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../../components/layouts/enhancement/TopContent";
import cnt from "../../../../services/definedConstant";
import { rightAction } from "../../../../redux/actions";
import {
  labelConfig,
  staticConfig,
  apiConfig,
} from "../../treasuryContractBooking.config";
import { BOM } from "../../../../services/definedMethod";

const RecordAuctionResults = (props) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams(); // auctionId

  const onFinish = async (values) => {
    try {
      setIsLoading(true);

      const payload = {
        results: (values.results || []).map((r) => ({
          tenor: r.tenor,
          acceptedAmount: r.acceptedAmount,
          weightedAveragePrice: r.weightedAveragePrice,
          weightedAverageYield: r.weightedAverageYield,
        })),
      };

      // await BOM.SendReq(apiConfig.recordAuctionResults, { auctionId: id, ...payload });
      console.log("RecordAuctionResults payload:", payload);
      BOM.AlertMsg(cnt.SUCCESS, "Auction results recorded successfully.");
      navigate(-1);
    } catch (error) {
      BOM.AlertMsg(cnt.DANGER, error?.message || "Failed to record auction results.");
    } finally {
      setIsLoading(false);
    }
  };

  // Label helper to keep JSX clean
  const FieldLabel = ({ text, tip }) => (
    <span>
      {text}
      <Tooltip title={tip}>
        <InfoCircleOutlined style={{ marginLeft: 4, color: "#667085" }} />
      </Tooltip>
    </span>
  );

  return (
    <MainLayout pageItems={staticConfig.newAuction}>
      <TopContent pageTitle={labelConfig.securityProduct} />

      <Spin spinning={isLoading} size="large">
        <div style={{ padding: "24px" }}>
          <Card
            style={{
              boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
              borderRadius: 20,
            }}
          >
            <h3 style={{ marginBottom: 24, fontWeight: 600 }}>
              Record Auction Results
            </h3>

            <Form
              layout="vertical"
              form={form}
              onFinish={onFinish}
              initialValues={{ results: [{}] }}
            >
              <Form.List name="results">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field, index) => (
                      <div key={field.key}>
                        {/* Row divider between entries */}
                        {index > 0 && (
                          <div
                            style={{
                              borderTop: "1px dashed #EAECF0",
                              margin: "4px 0 20px",
                            }}
                          />
                        )}

                        {/* Row label + remove button */}
                        <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
                          <Col>
                            <span
                              style={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: "#344054",
                              }}
                            >
                              Result Line {index + 1}
                            </span>
                          </Col>
                          {fields.length > 1 && (
                            <Col>
                              <button
                                type="button"
                                onClick={() => remove(field.name)}
                                style={{
                                  height: 32,
                                  padding: "0 12px",
                                  borderRadius: 6,
                                  border: "1px solid #FDA29B",
                                  background: "#FFF",
                                  color: "#B42318",
                                  fontWeight: 600,
                                  fontSize: 13,
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                }}
                              >
                                <DeleteOutlined style={{ fontSize: 13 }} />
                                Remove
                              </button>
                            </Col>
                          )}
                        </Row>

                        {/* Tenor & Accepted Amount */}
                        <Row>
                          <Col span={12} style={{ padding: "0 8px" }}>
                            <Form.Item
                              label={
                                <FieldLabel
                                  text="Tenor (Days)"
                                  tip="The tenor in days for this result line."
                                />
                              }
                              name={[field.name, "tenor"]}
                              rules={[{ required: true, message: "Tenor is required" }]}
                            >
                              <InputNumber
                                style={{ width: "100%", height: 44 }}
                                placeholder="e.g. 91"
                                min={1}
                                precision={0}
                              />
                            </Form.Item>
                          </Col>

                          <Col span={12} style={{ padding: "0 8px" }}>
                            <Form.Item
                              label={
                                <FieldLabel
                                  text="Accepted Amount"
                                  tip="The total amount accepted at this tenor."
                                />
                              }
                              name={[field.name, "acceptedAmount"]}
                              rules={[{ required: true, message: "Accepted amount is required" }]}
                            >
                              <InputNumber
                                style={{ width: "100%", height: 44 }}
                                placeholder="e.g. 450,000,000.00"
                                formatter={(value) =>
                                  value
                                    ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                    : ""
                                }
                                parser={(value) => value?.replace(/,/g, "")}
                                precision={2}
                                min={0}
                              />
                            </Form.Item>
                          </Col>
                        </Row>

                        {/* Weighted Avg Price & Yield */}
                        <Row>
                          <Col span={12} style={{ padding: "0 8px" }}>
                            <Form.Item
                              label={
                                <FieldLabel
                                  text="Weighted Average Price"
                                  tip="The weighted average price per 100 face value for accepted bids."
                                />
                              }
                              name={[field.name, "weightedAveragePrice"]}
                              rules={[{ required: true, message: "Weighted average price is required" }]}
                            >
                              <InputNumber
                                style={{ width: "100%", height: 44 }}
                                placeholder="e.g. 98.30"
                                precision={2}
                                min={0}
                                max={100}
                              />
                            </Form.Item>
                          </Col>

                          <Col span={12} style={{ padding: "0 8px" }}>
                            <Form.Item
                              label={
                                <FieldLabel
                                  text="Weighted Average Yield (%)"
                                  tip="The weighted average yield for accepted bids at this tenor."
                                />
                              }
                              name={[field.name, "weightedAverageYield"]}
                              rules={[{ required: true, message: "Weighted average yield is required" }]}
                            >
                              <InputNumber
                                style={{ width: "100%", height: 44 }}
                                placeholder="e.g. 12.70"
                                precision={2}
                                min={0}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      </div>
                    ))}

                    {/* Add result line */}
                    <Row style={{ marginTop: 4 }}>
                      <Col span={12} style={{ padding: "0 8px" }}>
                        <button
                          type="button"
                          onClick={() => add()}
                          style={{
                            width: "100%",
                            height: 44,
                            borderRadius: 8,
                            border: "1px dashed #D0D5DD",
                            background: "#F9FAFB",
                            color: "#344054",
                            fontWeight: 600,
                            fontSize: 14,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 8,
                          }}
                        >
                          <PlusOutlined />
                          Add Result Line
                        </button>
                      </Col>
                    </Row>
                  </>
                )}
              </Form.List>

              {/* Divider */}
              <div style={{ borderTop: "1px solid #EAECF0", margin: "24px 0 20px" }} />

              {/* Action Buttons */}
              <Row justify="end">
                <Col>
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    style={{
                      height: 44,
                      padding: "0 24px",
                      borderRadius: 8,
                      border: "1px solid #D0D5DD",
                      background: "#fff",
                      color: "#344054",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                      marginRight: 12,
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => form.submit()}
                    style={{
                      height: 44,
                      padding: "0 24px",
                      borderRadius: 8,
                      border: "1px solid #BA24D5",
                      background: "#BA24D5",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                    }}
                  >
                    Submit
                  </button>
                </Col>
              </Row>

              <button type="submit" hidden />
            </Form>
          </Card>
        </div>
      </Spin>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  rightBtn: state.RightSideBtn,
});

export default connect(mapStateToProps, { rightAction })(RecordAuctionResults);